import React, { useState } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { ABLE_COLORS, API_URL } from '../constants';
import MabelLogo from './MabelLogo';
import { 
  CircleDashed, 
  Compass, 
  BatteryLow,
  ArrowRight,
  Check,
  Sparkles,
  Brain,
  Lightbulb,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';

const INTEREST_OPTIONS = [
  { id: 'juggling', label: 'Juggling too much', icon: CircleDashed },
  { id: 'starting', label: "Don't know where to start", icon: Compass },
  { id: 'doing', label: "Can't make myself do it", icon: BatteryLow }
];

export default function LandingPage() {
  const { login, register } = useKindeAuth();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error', 'exists'
  const [errorMessage, setErrorMessage] = useState('');

  const toggleInterest = (id) => {
    setSelectedInterests(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setErrorMessage('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim(),
          interests: selectedInterests
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus(data.alreadyExists ? 'exists' : 'success');
        if (!data.alreadyExists) {
          setName('');
          setEmail('');
          setSelectedInterests([]);
        }
      } else {
        setErrorMessage(data.error || 'Something went wrong');
        setSubmitStatus('error');
      }
    } catch (err) {
      setErrorMessage('Failed to connect. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: ABLE_COLORS.bg,
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Navigation */}
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MabelLogo size={28} color={ABLE_COLORS.primary} />
          <span style={{ 
            fontSize: '20px', 
            fontWeight: '700', 
            color: ABLE_COLORS.textPrimary 
          }}>
            Mabel
          </span>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => login()}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              color: ABLE_COLORS.textSecondary,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => register()}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white',
              background: ABLE_COLORS.primary,
              border: 'none',
              borderRadius: ABLE_COLORS.buttonRadius,
              cursor: 'pointer'
            }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 40px 40px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: 'clamp(36px, 6vw, 56px)',
          fontWeight: '800',
          color: ABLE_COLORS.textPrimary,
          marginBottom: '24px',
          lineHeight: '1.1'
        }}>
          Get it out of your head.
        </h1>
        <p style={{
          fontSize: 'clamp(18px, 3vw, 22px)',
          color: ABLE_COLORS.textSecondary,
          maxWidth: '600px',
          margin: '0 auto 48px',
          lineHeight: '1.6'
        }}>
          Mabel holds what you're juggling and helps you figure out what to do next.
        </p>
      </section>

      {/* Waitlist Form Section */}
      <section style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 40px 80px'
      }}>
        {submitStatus === 'success' ? (
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '48px',
            textAlign: 'center',
            boxShadow: ABLE_COLORS.cardShadow
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: `${ABLE_COLORS.primary}15`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Sparkles size={32} color={ABLE_COLORS.primary} />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary,
              marginBottom: '12px'
            }}>
              You're on the list!
            </h2>
            <p style={{
              fontSize: '16px',
              color: ABLE_COLORS.textSecondary,
              marginBottom: '24px'
            }}>
              We'll let you know as soon as early access opens up.
            </p>
            <button
              onClick={() => setSubmitStatus(null)}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: ABLE_COLORS.primary,
                background: 'transparent',
                border: `2px solid ${ABLE_COLORS.primary}`,
                borderRadius: ABLE_COLORS.buttonRadius,
                cursor: 'pointer'
              }}
            >
              Back to page
            </button>
          </div>
        ) : submitStatus === 'exists' ? (
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '48px',
            textAlign: 'center',
            boxShadow: ABLE_COLORS.cardShadow
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: `${ABLE_COLORS.primary}15`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Check size={32} color={ABLE_COLORS.primary} />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary,
              marginBottom: '12px'
            }}>
              You're already on the list!
            </h2>
            <p style={{
              fontSize: '16px',
              color: ABLE_COLORS.textSecondary,
              marginBottom: '24px'
            }}>
              We've got your email saved. We'll be in touch soon.
            </p>
            <button
              onClick={() => setSubmitStatus(null)}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: ABLE_COLORS.primary,
                background: 'transparent',
                border: `2px solid ${ABLE_COLORS.primary}`,
                borderRadius: ABLE_COLORS.buttonRadius,
                cursor: 'pointer'
              }}
            >
              Back to page
            </button>
          </div>
        ) : (
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '40px',
            boxShadow: ABLE_COLORS.cardShadow
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary,
              marginBottom: '8px',
              textAlign: 'center'
            }}>
              Join the Waitlist
            </h2>
            <p style={{
              fontSize: '14px',
              color: ABLE_COLORS.textMuted,
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              Be first to get access when we launch.
            </p>

            <form onSubmit={handleSubmit}>
              {/* Name field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary,
                  marginBottom: '6px'
                }}>
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    background: ABLE_COLORS.neutral,
                    color: ABLE_COLORS.textPrimary,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Email field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary,
                  marginBottom: '6px'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: `1px solid ${errorMessage ? ABLE_COLORS.semantic.error : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    background: ABLE_COLORS.neutral,
                    color: ABLE_COLORS.textPrimary,
                    boxSizing: 'border-box'
                  }}
                />
                {errorMessage && (
                  <p style={{
                    fontSize: '13px',
                    color: ABLE_COLORS.semantic.error,
                    marginTop: '6px'
                  }}>
                    {errorMessage}
                  </p>
                )}
              </div>

              {/* Interest cards */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary,
                  marginBottom: '12px'
                }}>
                  What weighs on you most?
                </label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  {INTEREST_OPTIONS.map(option => {
                    const Icon = option.icon;
                    const isSelected = selectedInterests.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleInterest(option.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '14px 18px',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary,
                          background: isSelected ? `${ABLE_COLORS.primary}10` : ABLE_COLORS.neutral,
                          border: `2px solid ${isSelected ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                          borderRadius: ABLE_COLORS.radiusMedium,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          width: '100%',
                          textAlign: 'left'
                        }}
                      >
                        <Icon size={20} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  background: isSubmitting 
                    ? ABLE_COLORS.textMuted 
                    : `linear-gradient(135deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`,
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
                {!isSubmitting && <ArrowRight size={18} />}
              </button>

              <p style={{
                fontSize: '12px',
                color: ABLE_COLORS.textMuted,
                textAlign: 'center',
                marginTop: '16px'
              }}>
                No spam. Just updates on launch and occasional peeks behind the scenes.
              </p>
            </form>
          </div>
        )}
      </section>

      {/* How Mabel Helps Section */}
      <section style={{
        padding: '80px 40px',
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: 'clamp(24px, 4vw, 32px)',
          fontWeight: '700',
          color: ABLE_COLORS.textPrimary,
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          How Mabel helps
        </h2>
        <p style={{
          fontSize: '17px',
          color: ABLE_COLORS.textSecondary,
          textAlign: 'center',
          marginBottom: '48px',
          maxWidth: '600px',
          margin: '0 auto 48px'
        }}>
          Three ways Mabel takes the weight off your shoulders.
        </p>

        {/* 3-Step Visual Journey */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '48px'
        }}>
          {/* Step 1: Understand */}
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '32px',
            textAlign: 'center',
            boxShadow: ABLE_COLORS.cardShadow
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: `${ABLE_COLORS.state.mental}15`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Brain size={32} color={ABLE_COLORS.state.mental} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary,
              marginBottom: '12px'
            }}>
              Understand
            </h3>
            <p style={{
              fontSize: '15px',
              color: ABLE_COLORS.textSecondary,
              lineHeight: '1.6'
            }}>
              Mabel holds everything you're juggling - tasks, meals, schedules - so you don't have to keep it all in your head.
            </p>
          </div>

          {/* Step 2: Decide */}
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '32px',
            textAlign: 'center',
            boxShadow: ABLE_COLORS.cardShadow
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: `${ABLE_COLORS.accent}15`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Lightbulb size={32} color={ABLE_COLORS.accent} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary,
              marginBottom: '12px'
            }}>
              Decide
            </h3>
            <p style={{
              fontSize: '15px',
              color: ABLE_COLORS.textSecondary,
              lineHeight: '1.6'
            }}>
              Feeling stuck? Mabel looks at your energy, your situation, and suggests one thing to do next.
            </p>
          </div>

          {/* Step 3: Act */}
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '32px',
            textAlign: 'center',
            boxShadow: ABLE_COLORS.cardShadow
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: `${ABLE_COLORS.primary}15`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <CheckCircle2 size={32} color={ABLE_COLORS.primary} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary,
              marginBottom: '12px'
            }}>
              Act
            </h3>
            <p style={{
              fontSize: '15px',
              color: ABLE_COLORS.textSecondary,
              lineHeight: '1.6'
            }}>
              Can't start? Mabel breaks it down, stays with you, and helps you actually get it done.
            </p>
          </div>
        </div>

        {/* Flow arrows - visible on larger screens */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: `${ABLE_COLORS.state.mental}10`,
            borderRadius: ABLE_COLORS.radiusSmall,
            color: ABLE_COLORS.state.mental,
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <Brain size={16} />
            Mabel remembers it
          </div>
          <ChevronRight size={20} color={ABLE_COLORS.textMuted} />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: `${ABLE_COLORS.accent}10`,
            borderRadius: ABLE_COLORS.radiusSmall,
            color: ABLE_COLORS.accent,
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <Lightbulb size={16} />
            Mabel suggests
          </div>
          <ChevronRight size={20} color={ABLE_COLORS.textMuted} />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: `${ABLE_COLORS.primary}10`,
            borderRadius: ABLE_COLORS.radiusSmall,
            color: ABLE_COLORS.primary,
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <CheckCircle2 size={16} />
            Mabel helps
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section style={{
        background: ABLE_COLORS.bgSubtle,
        padding: '80px 40px'
      }}>
        <div style={{
          maxWidth: '700px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: 'clamp(24px, 4vw, 32px)',
            fontWeight: '700',
            color: ABLE_COLORS.textPrimary,
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            You're not disorganized. You're overloaded.
          </h2>
          <p style={{
            fontSize: '17px',
            color: ABLE_COLORS.textSecondary,
            lineHeight: '1.7',
            textAlign: 'center'
          }}>
            You've got a meal planning app. A task manager. A habit tracker. A family calendar. 
            Notes scattered everywhere. A collection of notebooks and bullet journals. And somehow you're still the one keeping it all running in your head.
          </p>
          <p style={{
            fontSize: '17px',
            color: ABLE_COLORS.textSecondary,
            lineHeight: '1.7',
            textAlign: 'center',
            marginTop: '16px'
          }}>
            The mental load doesn't live in any app. It lives in you - remembering what's for dinner, 
            who needs what, what's falling through the cracks, and whether you have the energy to deal with any of it.
          </p>
          <p style={{
            fontSize: '18px',
            color: ABLE_COLORS.textPrimary,
            fontWeight: '600',
            lineHeight: '1.7',
            textAlign: 'center',
            marginTop: '24px'
          }}>
            You don't need another app to manage. You need one place that actually helps.
          </p>
        </div>
      </section>

      {/* The Safety Section - NEW */}
      <section style={{
        padding: '80px 40px',
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: 'clamp(24px, 4vw, 32px)',
          fontWeight: '700',
          color: ABLE_COLORS.textPrimary,
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          Starting shouldn't feel this hard.
        </h2>
        <p style={{
          fontSize: '17px',
          color: ABLE_COLORS.textSecondary,
          lineHeight: '1.7',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto 16px'
        }}>
          You know what you need to do. You just can't make yourself start.
        </p>
        <p style={{
          fontSize: '17px',
          color: ABLE_COLORS.textSecondary,
          lineHeight: '1.7',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto 40px'
        }}>
          This isn't laziness. It's your nervous system protecting you from overwhelm.
        </p>

        {/* Safety features grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '40px'
        }}>
          {[
            { icon: '1', text: 'One suggestion, not a list' },
            { icon: '✓', text: 'Breaks it into easy steps' },
            { icon: '✨', text: 'Celebrates small wins' }
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 20px',
              background: ABLE_COLORS.neutral,
              borderRadius: ABLE_COLORS.radiusMedium,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: `${ABLE_COLORS.primary}15`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: ABLE_COLORS.primary,
                fontWeight: '700',
                fontSize: '14px'
              }}>
                {item.icon}
              </div>
              <span style={{
                fontSize: '15px',
                color: ABLE_COLORS.textPrimary,
                fontWeight: '500'
              }}>
                {item.text}
              </span>
            </div>
          ))}
        </div>

        <p style={{
          fontSize: '18px',
          color: ABLE_COLORS.textPrimary,
          fontWeight: '600',
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          Mabel makes starting easy.
        </p>
      </section>

      {/* Who It's For Section */}
      <section style={{
        padding: '80px 40px',
        maxWidth: '700px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: 'clamp(24px, 4vw, 32px)',
          fontWeight: '700',
          color: ABLE_COLORS.textPrimary,
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          Built for people carrying the invisible load.
        </h2>
        <p style={{
          fontSize: '17px',
          color: ABLE_COLORS.textSecondary,
          lineHeight: '1.7',
          textAlign: 'center'
        }}>
          Mabel is for anyone who's tired of being the project manager of their own life. 
          The one who remembers everything. The one who keeps the household running while 
          also trying to keep themselves running.
        </p>
        <p style={{
          fontSize: '17px',
          color: ABLE_COLORS.textSecondary,
          lineHeight: '1.7',
          textAlign: 'center',
          marginTop: '16px'
        }}>
          Whether you're a parent juggling family logistics, someone whose brain works 
          differently than productivity apps expect, or just a human who's done with 
          app overload - Mabel meets you where you are.
        </p>
        <p style={{
          fontSize: '16px',
          color: ABLE_COLORS.textMuted,
          lineHeight: '1.7',
          textAlign: 'center',
          marginTop: '20px',
          fontStyle: 'italic'
        }}>
          Especially helpful for brains that need support, not pressure.
        </p>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${ABLE_COLORS.border}`,
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
          <MabelLogo size={24} color={ABLE_COLORS.primary} />
          <span style={{ fontWeight: '600', color: ABLE_COLORS.textPrimary }}>Mabel</span>
        </div>
        <p style={{
          fontSize: '14px',
          color: ABLE_COLORS.textMuted
        }}>
          Your calm command center for everyday life.
        </p>
        <p style={{
          fontSize: '12px',
          color: ABLE_COLORS.textMuted,
          marginTop: '16px'
        }}>
          © {new Date().getFullYear()} <a 
            href="https://www.romijade.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: ABLE_COLORS.textMuted, 
              textDecoration: 'underline' 
            }}
          >Romi Jade</a>
        </p>
      </footer>
    </div>
  );
}
